/**
 * Audio Optimizer & Device Tier Detection
 *
 * Analyzes device capabilities to recommend the optimal noise cancellation preset.
 */

export type AudioPreset = 'low' | 'medium' | 'high';

interface DeviceCapabilities {
  logicalProcessors: number;
  deviceMemory: number; // in GB, approximate
  userAgent: string;
}

export const getDeviceCapabilities = (): DeviceCapabilities => {
  return {
    logicalProcessors: navigator.hardwareConcurrency || 4,
    // @ts-ignore - deviceMemory is standard but not in all TS definitions
    deviceMemory: (navigator as any).deviceMemory || 4,
    userAgent: navigator.userAgent,
  };
};

/**
 * Determines the recommended preset based on hardware stats.
 *
 * Logic:
 * - High: > 6 Cores AND > 6GB RAM (DeepFilterNet ready)
 * - Medium: > 4 Cores (RNNoise ready)
 * - Low: Everything else (Native only)
 */
export const getRecommendedPreset = (): AudioPreset => {
  const caps = getDeviceCapabilities();

  // Mobile devices often report high cores but have thermal limits,
  // so we treat them conservatively unless they have high memory.
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(caps.userAgent);

  if (caps.logicalProcessors >= 8 && caps.deviceMemory >= 8 && !isMobile) {
    return 'high';
  }

  if (caps.logicalProcessors >= 4) {
    return 'medium';
  }

  return 'low';
};

/**
 * Returns optimal Web Audio constraints to minimize latency based on preset
 */
export const getAudioConstraints = (preset: AudioPreset): MediaTrackConstraints => {
  const baseConstraints: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: false, // We handle this manually for Mid/High
    autoGainControl: true, // Keep AGC on to prevent clipping before our processor
  };

  if (preset === 'low') {
    return {
      ...baseConstraints,
      noiseSuppression: true, // Use native for Low tier
    };
  }

  return baseConstraints;
};
